<template>
  <article class="card cl-raider-card">
    <header class="cl-card-head">
      <div>
        <p class="eyebrow">Raider.IO</p>
        <h3>Link this log to a Mythic+ run</h3>
        <p class="muted-line">
          Optional. The log records a localized realm name, so the character is matched against your saved characters
          rather than guessed, and a run is only linked when the dungeon id, keystone level and timing all agree.
        </p>
      </div>
      <div class="page-actions">
        <button
          v-if="linkedCharacter"
          class="btn-sm-outline"
          type="button"
          @click="$emit('unlink')"
        >
          Unlink
        </button>
        <button
          class="btn-sm-outline"
          type="button"
          :disabled="loading || !linkedCharacter || !runs.length"
          @click="loadRuns(true)"
        >
          <i class="fa-solid" :class="loading ? 'fa-spinner fa-spin' : 'fa-rotate'"></i>
          Refresh runs
        </button>
      </div>
    </header>

    <section class="cl-raider-owner">
      <div>
        <span>Log recorded by</span>
        <strong>{{ ownerLabel }}</strong>
      </div>
      <div>
        <span>Linked character</span>
        <strong>{{ linkedCharacter ? characterLabel(linkedCharacter) : 'Not linked' }}</strong>
      </div>
      <div>
        <span>Runs in this log</span>
        <strong>{{ logRuns.length }}</strong>
      </div>
    </section>

    <section v-if="!logOwner" class="cl-empty-line">
      This log has no owner information, so it cannot be matched to a character.
    </section>

    <section v-else-if="!linkedCharacter">
      <h4>Saved characters</h4>
      <p v-if="!candidates.length && !savedCharacters.length" class="cl-empty-line">
        Add the character to your favorites first, then it can be linked here.
      </p>
      <p v-else-if="!candidates.length" class="cl-empty-line">
        None of your saved characters matches {{ ownerLabel }} by name. Pick one manually if you know it is the same
        character.
      </p>
      <ul class="cl-candidate-list">
        <li v-for="candidate in offeredCharacters" :key="candidate.id">
          <button type="button" @click="$emit('link', candidate.id)">
            <span>
              <strong>{{ characterLabel(candidate) }}</strong>
              <small>{{ candidate.reasons ? `Matches by ${candidate.reasons.join(', ')}` : 'Manual choice' }}</small>
            </span>
            <i class="fa-solid fa-link"></i>
          </button>
        </li>
      </ul>
    </section>

    <section v-else>
      <p v-if="error" class="cl-warn-text">{{ error }}</p>
      <p v-else-if="loading" class="muted-line">Loading recent runs from Raider.IO...</p>
      <p v-else-if="!runs.length" class="cl-empty-line">
        Raider.IO has no recent or best runs for this character, so there is nothing to match against.
      </p>

      <ul v-if="matches.length" class="cl-match-list">
        <li v-for="match in matches" :key="match.key">
          <div class="cl-match-head">
            <strong>{{ match.logRun.name }} +{{ match.logRun.meta.keystoneLevel }}</strong>
            <span class="cl-chip" :class="statusClass(match.result.status)">{{ statusLabel(match.result.status) }}</span>
          </div>

          <div v-if="match.result.status === 'exact'" class="cl-match-body">
            <dl class="cl-definition-list is-tight">
              <div>
                <dt>Dungeon</dt>
                <dd>{{ match.result.run.dungeon }}</dd>
              </div>
              <div>
                <dt>Keystone</dt>
                <dd>+{{ match.result.run.mythic_level }}</dd>
              </div>
              <div>
                <dt>Clear time</dt>
                <dd>{{ formatDuration(match.result.run.clear_time_ms) }}</dd>
              </div>
              <div>
                <dt>Upgrades</dt>
                <dd>{{ upgradeLabel(match.result.run) }}</dd>
              </div>
              <div>
                <dt>Score</dt>
                <dd>{{ match.result.run.score ? match.result.run.score.toFixed(1) : NO_DATA }}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{{ formatDateTime(Date.parse(match.result.run.completed_at)) }}</dd>
              </div>
            </dl>
            <p class="muted-line">Matched by {{ match.result.reasons.join(', ') }}.</p>
            <a
              v-if="match.result.run.url"
              class="btn-sm-outline"
              :href="match.result.run.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open on Raider.IO
            </a>
          </div>

          <div v-else-if="match.result.status === 'candidate'" class="cl-match-body">
            <p class="muted-line">
              Several Raider.IO runs of this dungeon and level could be this log. Confirm the right one, or leave it
              unlinked.
            </p>
            <ul class="cl-candidate-list">
              <li v-for="option in match.result.candidates" :key="option.run.keystone_run_id || option.run.url">
                <button type="button" @click="confirm(match.key, option.run)">
                  <span>
                    <strong>{{ formatDuration(option.run.clear_time_ms) }} - {{ upgradeLabel(option.run) }}</strong>
                    <small>{{ formatDateTime(Date.parse(option.run.completed_at)) }} - matched by {{ option.reasons.join(', ') }}</small>
                  </span>
                  <i class="fa-solid fa-check"></i>
                </button>
              </li>
            </ul>
          </div>

          <p v-else class="muted-line">
            No Raider.IO run matches this one. Runs older than the character's recent history are not returned by the API.
          </p>
        </li>
      </ul>

      <p v-else-if="!loading && !error" class="cl-empty-line">
        This log has no completed Mythic+ run to match.
      </p>
    </section>
  </article>
</template>

<script>
import { getCharacterProfile } from '../../services/raiderApi'
import { findOwnerCandidates, matchChallengeRun } from '../../services/combatLog/raiderMatch'
import { NO_DATA, formatDuration, formatDateTime } from '../../services/combatLog/format'

const PROFILE_FIELDS = 'mythic_plus_recent_runs,mythic_plus_best_runs:all'

export default {
  name: 'CombatLogRaiderLink',
  props: {
    logOwner: { type: Object, default: null },
    logRuns: { type: Array, default: () => [] },
    savedCharacters: { type: Array, default: () => [] },
    linkedCharacterId: { type: String, default: '' },
  },
  emits: ['link', 'unlink'],
  data() {
    return {
      NO_DATA,
      loading: false,
      error: '',
      runs: [],
      confirmed: {},
    }
  },
  computed: {
    ownerLabel() {
      if (!this.logOwner) return NO_DATA
      const realm = this.logOwner.realm ? `-${this.logOwner.realm}` : ''
      return `${this.logOwner.name}${realm}`
    },
    candidates() {
      return findOwnerCandidates(this.logOwner, this.savedCharacters)
        .map((candidate) => ({
          id: candidate.entry.id,
          profile: candidate.profile,
          reasons: candidate.reasons,
        }))
    },
    offeredCharacters() {
      if (this.candidates.length) return this.candidates
      return this.savedCharacters.map((entry) => ({ id: entry.id, profile: entry.profile, reasons: null }))
    },
    linkedCharacter() {
      if (!this.linkedCharacterId) return null
      return this.savedCharacters.find((entry) => entry.id === this.linkedCharacterId) || null
    },
    completedRuns() {
      return this.logRuns.filter((run) => run.meta && run.meta.completed)
    },
    matches() {
      return this.completedRuns.map((logRun) => {
        const manual = this.confirmed[logRun.key]
        const result = manual
          ? { status: 'exact', run: manual, reasons: ['manual confirmation'] }
          : matchChallengeRun(logRun, this.runs)
        return { key: logRun.key, logRun, result }
      })
    },
  },
  watch: {
    linkedCharacterId: {
      immediate: true,
      handler(value) {
        this.runs = []
        this.confirmed = {}
        if (value) this.loadRuns()
      },
    },
  },
  methods: {
    formatDuration,
    formatDateTime,
    characterLabel(entry) {
      const profile = entry.profile || entry
      return `${profile.name} - ${profile.realm} (${String(profile.region || '').toUpperCase()})`
    },
    async loadRuns(force = false) {
      const entry = this.linkedCharacter
      if (!entry || (this.runs.length && !force)) return
      const profile = entry.profile || {}
      this.loading = true
      this.error = ''
      try {
        const response = await getCharacterProfile({
          region: profile.region,
          realm: profile.realm,
          name: profile.name,
          fields: PROFILE_FIELDS,
        }, { force })
        this.runs = [
          ...(response.mythic_plus_recent_runs || []),
          ...(response.mythic_plus_best_runs || []),
        ]
      } catch (error) {
        this.error = error.message || 'Raider.IO request failed'
      } finally {
        this.loading = false
      }
    },
    confirm(key, run) {
      this.confirmed = { ...this.confirmed, [key]: run }
    },
    statusLabel(status) {
      if (status === 'exact') return 'Linked'
      if (status === 'candidate') return 'Needs confirmation'
      return 'No match'
    },
    statusClass(status) {
      if (status === 'exact') return 'is-good'
      if (status === 'candidate') return 'is-subtle'
      return 'is-bad'
    },
    upgradeLabel(run) {
      const upgrades = Number(run.num_keystone_upgrades || 0)
      if (!upgrades) return 'Over time'
      return `+${upgrades}`
    },
  },
}
</script>
